/**
 * Configurazione dei risparmi per slider_all_bonus_banner
 * 
 * SHORT VERSION: CAP slide comes first, so when calculating bonus count
 * we need to show ALL bonuses (PV+HP combined) since product type is not selected yet
 */

import { 
  ProductType,
  BonusDisplayItem,
} from '../types';
import { formatCurrency } from '../utils/helpers';
import { 
  getBonusesForCap, 
  getTooltipTextForProduct,
  formatBonusForDisplay,
} from './bonusLookup';
import { getBenefitKeyForSlide } from './slidesConfig';

/**
 * Mappa gli indici delle slide ai nomi dei benefit
 * 4 benefit slides: install_timeline, name, email, phone
 */
const SLIDE_TO_BONUS_INDEX: Record<string, number> = {
  install_timeline: 0, // Benefit 1/5
  name_form: 1,        // Benefit 2/5
  email_form: 2,       // Benefit 3/5
  phone_form: 3,       // Benefit 4/5
};

/**
 * Ottiene il testo del tooltip in base al prodotto
 */
export function getTooltipText(productType?: ProductType): string {
  return getTooltipTextForProduct(productType);
}

/**
 * Ottiene il testo dinamico del conteggio bonus per una slide specifica
 * Es: "VANTAGGIO 1/5" dove il denominatore è dinamico
 * 
 * IMPORTANTE: La slide phone_form mostra SEMPRE "VANTAGGIO 4/5" per creare
 * l'effetto psicologico che c'è ancora un vantaggio da scoprire dopo.
 * 
 * @param slideId - ID della slide (install_timeline, name_form, email_form, phone_form)
 * @param productType - Tipo di prodotto
 * @param zipCode - Codice CAP per il lookup
 * @returns Testo del conteggio bonus (es: "VANTAGGIO 1/5")
 */
export function getBonusCountText(
  slideId: string,
  productType?: ProductType,
  zipCode?: string
): string {
  const benefitKey = getBenefitKeyForSlide(slideId);
  const bonusIndex = SLIDE_TO_BONUS_INDEX[benefitKey];
  
  if (bonusIndex === undefined) return '';
  
  // La slide telefono mostra SEMPRE "VANTAGGIO 4/5"
  // L'utente deve sempre pensare che ci sia un vantaggio successivo
  if (slideId === 'phone_form') {
    return 'VANTAGGIO 4/5';
  }
  
  // Per le altre slide, usa il conteggio dinamico ma con denominatore fisso a 5
  // per coerenza con la slide telefono
  return `VANTAGGIO ${bonusIndex + 1}/5`;
}

/**
 * Ottiene il testo del beneficio per una slide specifica
 * Usa il database CAP per valori dinamici
 * 
 * @param slideId - ID della slide (install_timeline, name_form, email_form, phone_form)
 * @param productType - Tipo di prodotto
 * @param _zipFlag - (deprecato, mantenuto per compatibilità)
 * @param zipCode - Codice CAP per il lookup
 * @returns Testo formattato del bonus o null
 */
export function getSavingsText(
  slideId: string,
  productType?: ProductType,
  _zipFlag?: boolean | null,
  zipCode?: string
): string | null {
  // Mappa l'ID slide alla chiave del beneficio
  const benefitKey = getBenefitKeyForSlide(slideId);
  const bonusIndex = SLIDE_TO_BONUS_INDEX[benefitKey];
  
  if (bonusIndex === undefined) return null;
  
  // Ottieni i bonus dal database CAP
  const bonuses = getBonusesForCap(zipCode, productType);
  const bonus = bonuses.slideBonus[bonusIndex];
  
  if (!bonus) return null;
  
  return formatBonusForDisplay(bonus.value, bonus.label);
}

/**
 * Calcola il risparmio totale cumulativo fino a una slide
 * 
 * @param slideId - ID della slide
 * @param productType - Tipo di prodotto
 * @param _zipFlag - (deprecato)
 * @param zipCode - Codice CAP
 * @returns Stringa formattata del totale cumulativo
 */
export function calculateTotalSavings(
  slideId: string,
  productType?: ProductType,
  _zipFlag?: boolean | null,
  zipCode?: string
): string {
  const benefitKey = getBenefitKeyForSlide(slideId);
  const currentIndex = SLIDE_TO_BONUS_INDEX[benefitKey];
  
  if (currentIndex === undefined) return '';
  
  const bonuses = getBonusesForCap(zipCode, productType);
  
  // Somma i bonus fino all'indice corrente
  let total = 0;
  for (let i = 0; i <= currentIndex && i < bonuses.slideBonus.length; i++) {
    total += bonuses.slideBonus[i].value;
  }
  
  return formatCurrency(total);
}

/**
 * Calcola il conteggio bonus e totale per il feedback CAP
 * Usato nella slide del CAP per mostrare "X bonus disponibili"
 * 
 * SHORT VERSION: Since CAP slide comes first (before product selection),
 * we count ALL bonuses (both PV and HP combined)
 */
export function calculateBonusSummary(
  productType?: ProductType,
  _zipFlag?: boolean | null,
  zipCode?: string
): { bonusCount: number; totalSavings: number } {
  // If no product type selected yet (CAP slide comes first),
  // count ALL bonuses (both PV and HP)
  if (!productType) {
    const bonusesPV = getBonusesForCap(zipCode, 'Photovoltaic');
    const bonusesHP = getBonusesForCap(zipCode, 'Heatpump');
    
    // Count unique bonuses (avoid double counting if same bonus exists for both)
    const combinedCount = bonusesPV.bonusCount + bonusesHP.bonusCount;
    
    return {
      bonusCount: combinedCount,
      totalSavings: bonusesPV.total + bonusesHP.total,
    };
  }
  
  // Otherwise use standard logic for specific product type
  const bonuses = getBonusesForCap(zipCode, productType);
  
  return {
    bonusCount: bonuses.bonusCount,
    totalSavings: bonuses.total,
  };
}

/**
 * Interfaccia per un item del riepilogo risparmi
 */
export interface SavingsSummaryItem {
  label: string;
  value: string;
  scadenza?: string | null;
  category?: 'PV' | 'HP';
}

/**
 * Genera i dati per il riepilogo finale dei risparmi
 * Usa tutti i bonus disponibili dal database CAP
 */
export function generateSavingsSummary(
  productType?: ProductType,
  _zipFlag?: boolean | null,
  zipCode?: string
): {
  items: SavingsSummaryItem[];
  total: number;
  co2Saved: string;
} {
  const bonuses = getBonusesForCap(zipCode, productType);
  
  // Converti i bonus in items per il riepilogo
  const items: SavingsSummaryItem[] = bonuses.summaryBonus.map((bonus: BonusDisplayItem) => ({
    label: bonus.label,
    value: formatCurrency(bonus.value),
    scadenza: bonus.scadenza,
    category: bonus.category,
  }));
  
  return {
    items,
    total: bonuses.total,
    co2Saved: 'Scoprilo in chiamata',
  };
}

// ============================================
// Esportazioni per retrocompatibilità
// (mantenute per evitare breaking changes in altri moduli)
// ============================================

/**
 * @deprecated Usare getTooltipText invece
 */
export const TOOLTIP_TEXTS = {
  Photovoltaic: getTooltipTextForProduct('Photovoltaic'),
  Heatpump: getTooltipTextForProduct('Heatpump'),
  Photovoltaic_Heatpump: getTooltipTextForProduct('Photovoltaic + Heatpump'),
};
